import { useEffect, useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, Mail, Phone } from 'lucide-react';

const STAGES = [
  { id: 'lead', label: 'Lead' },
  { id: 'nurture', label: 'Nurture' },
  { id: 'active', label: 'Active' },
  { id: 'under_contract', label: 'Under Contract' },
  { id: 'closed', label: 'Closed' },
];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', stage: 'lead', type: 'buyer', budget: '', location: '', notes: '' });

  const load = async () => {
    try {
      const { data } = await http.get('/clients');
      setClients(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await http.post('/clients', form);
      setClients([data, ...clients]);
      setForm({ name: '', email: '', phone: '', stage: 'lead', type: 'buyer', budget: '', location: '', notes: '' });
      setShowForm(false);
      toast.success('Client added.');
    } catch (e) { toast.error(formatError(e)); }
  };

  const updateStage = async (c, stage) => {
    try {
      const { data } = await http.patch(`/clients/${c.id}`, { stage });
      setClients(clients.map(x => x.id === c.id ? data : x));
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await http.delete(`/clients/${id}`); setClients(clients.filter(x => x.id !== id)); }
    catch (e) { toast.error(formatError(e)); }
  };

  const filtered = clients.filter(c =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.location||'').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Clientele</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="clients-title">Your People</h1>
        <div className="hq-divider-gold w-fit"></div>
      </header>

      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
        <input
          className="hq-input sm:max-w-md"
          placeholder="Search by name or neighborhood…"
          value={q}
          onChange={e => setQ(e.target.value)}
          data-testid="client-search"
        />
        <button className="hq-btn-primary shrink-0" onClick={() => setShowForm(!showForm)} data-testid="new-client-btn">
          {showForm ? 'Cancel' : '+ New Client'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="hq-card p-8 grid grid-cols-1 md:grid-cols-2 gap-5 hq-fade-in">
          <div><label className="hq-label">Name</label><input className="hq-input" required data-testid="client-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><label className="hq-label">Type</label>
            <select className="hq-input" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <option value="buyer">Buyer</option><option value="seller">Seller</option><option value="both">Both</option>
            </select>
          </div>
          <div><label className="hq-label">Email</label><input className="hq-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div><label className="hq-label">Phone</label><input className="hq-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
          <div><label className="hq-label">Budget</label><input className="hq-input" value={form.budget} onChange={e => setForm({...form, budget: e.target.value})} placeholder="$1.2M – $1.6M" /></div>
          <div><label className="hq-label">Preferred Location</label><input className="hq-input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
          <div><label className="hq-label">Stage</label>
            <select className="hq-input" value={form.stage} onChange={e => setForm({...form, stage: e.target.value})}>
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="hq-label">Notes</label><textarea className="hq-input" rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          <div className="md:col-span-2 text-right">
            <button className="hq-btn-primary" data-testid="save-client-btn">Save Client</button>
          </div>
        </form>
      )}

      <div className="hq-card overflow-hidden">
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <table className="w-full min-w-[720px]">
          <thead style={{ background: 'var(--parchment)', borderBottom: '1px solid var(--line)' }}>
            <tr>
              {['Name', 'Type', 'Budget', 'Location', 'Stage', 'Contact', ''].map(h => (
                <th key={h} className="text-left px-6 py-4 hq-eyebrow">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="clients-list">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-16 text-center" style={{ color: 'var(--muted)' }}>
                <div className="font-serif text-xl" style={{ color: 'var(--sage)' }}>No clients yet.</div>
                <div className="text-sm mt-1">Add your first — your relationships, quietly kept.</div>
              </td></tr>
            )}
            {filtered.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--line-soft)' }} data-testid={`client-row-${c.id}`}>
                <td className="px-6 py-4 font-serif text-lg" style={{ color: 'var(--sage)' }}>{c.name}</td>
                <td className="px-6 py-4 text-xs uppercase tracking-widest" style={{ color: 'var(--muted)' }}>{c.type}</td>
                <td className="px-6 py-4 text-sm" style={{ color: 'var(--ink-soft)' }}>{c.budget || '—'}</td>
                <td className="px-6 py-4 text-sm" style={{ color: 'var(--ink-soft)' }}>{c.location || '—'}</td>
                <td className="px-6 py-4">
                  <select
                    className="text-xs px-2 py-1"
                    value={c.stage}
                    onChange={e => updateStage(c, e.target.value)}
                    style={{ background: 'var(--parchment)', border: '1px solid var(--line)', borderRadius: 2 }}
                    data-testid={`client-stage-${c.id}`}
                  >
                    {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-6 py-4 text-xs" style={{ color: 'var(--muted)' }}>
                  {c.email && <div className="flex items-center gap-1"><Mail size={11} />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-1"><Phone size={11} />{c.phone}</div>}
                </td>
                <td className="px-6 py-4 text-right"><button onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 size={14} style={{ color: 'var(--muted)' }} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
